/**
 * Full-bleed hero video for the project landing page.
 *
 * Rendering contract:
 *   - The poster `<img>` is always rendered eagerly underneath (it is the
 *     LCP element), so first paint never waits on the video.
 *   - The `<video>` is `autoplay muted loop playsInline`; its `src` is
 *     attached after mount (`preload="none"` until then) so the request
 *     never competes with first paint, and it fades in once frames are
 *     ready.
 *   - Reduced-motion visitors, Save-Data, and slow connections get the
 *     poster only.
 *   - Playback pauses whenever the hero scrolls out of view.
 *   - A mute toggle (bottom-right) lets a visitor opt in to sound.
 */
import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { usePrefersReducedMotion } from "@/components/project-motion";

export type ProjectHeroVideoSource = {
  src: string;
  poster: string | null;
  contentType?: string | null;
};

type NetworkInformationLike = {
  saveData?: boolean;
  effectiveType?: string;
};

/** True when the visitor has asked for lighter pages or is on a slow link. */
export function isConstrainedConnection(
  nav:
    { connection?: NetworkInformationLike } | undefined = typeof navigator !==
  "undefined"
    ? (navigator as unknown as { connection?: NetworkInformationLike })
    : undefined,
): boolean {
  const connection = nav?.connection;
  if (!connection) return false;
  if (connection.saveData) return true;
  const type = connection.effectiveType;
  return type === "slow-2g" || type === "2g";
}

/** Decide whether the video should play at all for this visitor. */
export function shouldPlayHeroVideo(input: {
  reducedMotion: boolean;
  constrainedConnection: boolean;
}): boolean {
  return !input.reducedMotion && !input.constrainedConnection;
}

export function ProjectHeroVideo({
  video,
  alt,
  className = "relative",
  posterClassName = "",
  imgWidth,
  imgHeight,
  showMuteToggle = true,
  fit = "cover",
  testId = "project-hero-video",
}: {
  video: ProjectHeroVideoSource;
  alt: string;
  /** Positioning/size classes for the frame (callers pass `absolute inset-0` to fill a hero). */
  className?: string;
  posterClassName?: string;
  imgWidth?: number;
  imgHeight?: number;
  showMuteToggle?: boolean;
  /**
   * `cover` is the full-bleed landing-page hero. `contain` keeps the whole
   * frame visible inside a pitch slide (letterboxed, not cropped).
   */
  fit?: "cover" | "contain";
  testId?: string;
}) {
  const reducedMotion = usePrefersReducedMotion();
  const [constrained] = useState(() => isConstrainedConnection());
  const playVideo = shouldPlayHeroVideo({
    reducedMotion,
    constrainedConnection: constrained,
  });

  const videoRef = useRef<HTMLVideoElement>(null);
  const [sourceAttached, setSourceAttached] = useState(false);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [muted, setMuted] = useState(true);
  const [inView, setInView] = useState(true);

  // A replaced source (admin swaps the file) starts from a clean slate.
  useEffect(() => {
    setReady(false);
    setFailed(false);
  }, [video.src]);

  // Attach the source only after mount so the poster paints first.
  useEffect(() => {
    if (!playVideo) return;
    const id = window.setTimeout(() => setSourceAttached(true), 0);
    return () => window.clearTimeout(id);
  }, [playVideo]);

  const showVideo = playVideo && sourceAttached && !failed;
  // Reduced motion with no poster still shows the file: paused on the
  // first frame, never autoplaying. A poster, when one exists, stays the
  // still. Constrained connections do not download the file.
  const showStillFrame = reducedMotion && !constrained && !video.poster && !failed;
  const fitClass = fit === "contain" ? "object-contain" : "object-cover";

  // Pause when scrolled out of view; resume when back.
  useEffect(() => {
    const node = videoRef.current;
    if (!node || !showVideo || typeof IntersectionObserver === "undefined")
      return;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.1 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [showVideo]);

  useEffect(() => {
    const node = videoRef.current;
    if (!node || !showVideo) return;
    if (inView) {
      try {
        const result = node.play();
        if (result && typeof (result as Promise<void>).catch === "function") {
          (result as Promise<void>).catch(() => {});
        }
      } catch {
        /* autoplay refused — poster stays visible */
      }
    } else {
      try {
        node.pause();
      } catch {
        /* ignore */
      }
    }
  }, [inView, showVideo]);

  useEffect(() => {
    const node = videoRef.current;
    if (node) node.muted = muted;
  }, [muted, showVideo]);

  return (
    <div className={`overflow-hidden ${className}`} data-testid={testId}>
      {video.poster ? (
        <img
          src={video.poster}
          alt={alt}
          width={imgWidth}
          height={imgHeight}
          fetchPriority="high"
          loading="eager"
          decoding="async"
          className={`absolute inset-0 h-full w-full ${fitClass} object-center ${posterClassName}`}
          data-testid="project-hero-poster"
        />
      ) : (
        <div className="absolute inset-0 bg-[#061828]" aria-hidden="true" />
      )}
      {showVideo ? (
        <video
          ref={videoRef}
          src={video.src}
          className={`absolute inset-0 h-full w-full ${fitClass} object-center transition-opacity duration-700 ease-out ${
            ready ? "opacity-100" : "opacity-0"
          }`}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={video.poster ?? undefined}
          aria-label={alt}
          tabIndex={-1}
          disablePictureInPicture
          onPlaying={() => setReady(true)}
          onCanPlay={() => setReady(true)}
          onError={() => setFailed(true)}
          data-testid="project-hero-video-element"
          data-playback="loop"
        />
      ) : null}
      {showStillFrame ? (
        <video
          src={video.src}
          className={`absolute inset-0 h-full w-full ${fitClass} object-center`}
          muted
          playsInline
          preload="auto"
          aria-label={alt}
          tabIndex={-1}
          disablePictureInPicture
          onLoadedData={(event) => {
            try {
              event.currentTarget.pause();
            } catch {
              /* first frame stays */
            }
          }}
          onError={() => setFailed(true)}
          data-testid="project-hero-video-element"
          data-playback="still"
        />
      ) : null}
      {showVideo && showMuteToggle ? (
        <button
          type="button"
          onClick={() => setMuted((m) => !m)}
          aria-pressed={!muted}
          aria-label={muted ? "Unmute hero video" : "Mute hero video"}
          className="absolute right-4 top-4 z-20 grid h-10 w-10 place-items-center rounded-full border border-white/25 bg-black/45 text-white backdrop-blur transition-colors hover:bg-black/65 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D7B85A] sm:right-6 sm:top-6"
          data-testid="button-hero-video-mute"
        >
          {muted ? (
            <VolumeX className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Volume2 className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      ) : null}
    </div>
  );
}
